import React, { useState, useCallback, useEffect } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, useMediaQuery } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import * as Styles from './style';
import { Icon } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import theme from 'themes/dark-theme';
import MaterialIcon from '@mui/material/Icon';
import ClickNHold from 'react-click-n-hold';
import { Delete } from 'assets/css/delete-animation';
import Tooltip from 'components/Tooltip';
import { getCountryImage } from 'components/manage-vm/utils';
import { userRights } from 'store/selectors/user-details';
import moduleClasses from 'components/manage-vm/manage-vm.module.css';
import { base64Label, checkDeletedItems, menuOptions, whDropDownItems } from './util';
import SlidingPanels from 'components/common/sliding-panel';
import { WhNetworks } from './WhNetworks';
import SettingPanel from './SettingPanel';
import { ServerImage } from './ServerImage';
import { H5, P } from 'components/reusable-css';
import CustomButton from 'components/common/custom-button';

const ManageWhMobile = ({ data, managerWhRefetch, managerVmIsFetching, setDeletedIds, deletedIds }) => {
  const [openConfigurationPanel, setOpenConfigurationPanel] = useState(false);
  const [openAccordian, setOpenAccordian] = useState(false);
  const [rowID, setRowId] = useState();
  const [anim, setAnim] = useState();
  const { t } = useTranslation();
  const match = useMediaQuery('(max-width:550px)');
  const toggle = () => setOpenConfigurationPanel((prev) => !prev);
  const toggleAccordian = () => setOpenAccordian((prev) => !prev);
  const checkDisabledItem = checkDeletedItems(deletedIds, data?.id);
  const rights = useSelector(userRights);

  const escFunction = useCallback((event) => {
    if (event.key === 'Escape') {
      setOpenConfigurationPanel(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', escFunction, false);

    return () => {
      document.removeEventListener('keydown', escFunction, false);
    };
  }, []);

  return (
    <Styles.MobileRoot style={checkDisabledItem}>
      <Accordion style={{ borderRadius: '10px' }} elevation={0}>
        <AccordionSummary
          onClick={() => {
            setRowId(data?.id);
            toggleAccordian();
          }}
          style={{ padding: '0 10px 0 0' }}
          expandIcon={<ExpandMoreIcon fontSize="large" style={{ color: 'orange' }} />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <Styles.MobilePicture>
            <ServerImage data={data} />
          </Styles.MobilePicture>
          <div className={moduleClasses.width}>
            <Styles.Label>
              <div className={moduleClasses.tag_heading_icon}>
                <H5>{base64Label(data.label)}</H5>
                <Icon className={moduleClasses.pencil_icon} name="pencil" />
              </div>
            </Styles.Label>
            <div>
              <WhNetworks data={data} />
            </div>
            <Styles.SummaryLastRow>
              <Styles.CountryFlagMobile>
                <Styles.Circle style={{ border: '0.5px solid #C5C5C5', width: '20px', height: '20px' }}>
                  <img src={getCountryImage(data?.region, data?.region)} alt="country" />
                </Styles.Circle>
                <P>{data?.region}</P>
                {data?.status && <Styles.Status status={data?.status}>{data?.status}</Styles.Status>}
              </Styles.CountryFlagMobile>
            </Styles.SummaryLastRow>
          </div>
        </AccordionSummary>
        <AccordionDetails
          style={{
            padding: '1rem 1.4rem 1rem 2rem',
            borderTop: '1px solid lightGrey',
            display: 'block',
            marginBottom: '5rem',
          }}
        >
          <Styles.MobileCardDescription>
            <Styles.WebsiteCount>
              <P style={{ padding: '10px 7px' }}>
                {t(data?.websites_count > 1 ? 'wh/instance.string11' : 'wh/instance.string10', {
                  websites_count: data?.websites_count,
                  websites_limit: data?.websites_limit,
                })}
              </P>
            </Styles.WebsiteCount>
            <Styles.Backup>
              <CustomButton title={t('wh/instance.string9')} buttonStyles={{ padding: '10px 7px', fontSize: '12px' }} />
            </Styles.Backup>
          </Styles.MobileCardDescription>
        </AccordionDetails>
      </Accordion>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row-reverse',
        }}
      >
        {rowID == data.id && openAccordian && (
          <Styles.MobileOptions>
            <div
              style={{
                padding: '1rem 0',
              }}
            >
              {whDropDownItems(setOpenConfigurationPanel).map((dropDownItem) => (
                <React.Fragment key={dropDownItem.id}>
                  {dropDownItem.name != 'delete_VS' ? (
                    !(dropDownItem.name == 'reboot' && !rights.vsreboot) && (
                      <Styles.MobileMenuItem
                        onClick={() => {
                          if (dropDownItem.name == 'settings') {
                            managerWhRefetch();
                          }
                          dropDownItem.onClick();
                        }}
                      >
                        <div style={{ paddingTop: 5, padding: '0 1.6rem' }} className="d-flex">
                          <MaterialIcon
                            style={{ width: '2rem', marginRight: '1rem', color: theme.primaryDarkColor }}
                            className={dropDownItem.icon}
                          />
                          <p>{t(dropDownItem.lang)}</p>
                        </div>
                      </Styles.MobileMenuItem>
                    )
                  ) : (
                    <ClickNHold
                      time={2}
                      onStart={() => setAnim(true)}
                      onEnd={() => setAnim(false)}
                      onClickNHold={() => {
                        dropDownItem.onClick();
                        setDeletedIds([...deletedIds, data.id]);
                      }}
                    >
                      <Tooltip title="Click for 2 seconds" placement="left">
                        <Delete anim={anim} className="icon d-flex" style={{ padding: '7px 2.1rem' }}>
                          <MaterialIcon
                            style={{ width: '2rem', marginRight: '1rem', color: theme.primaryDarkColor }}
                            className={dropDownItem.icon}
                          />
                          <p>{t(dropDownItem.lang)}</p>
                        </Delete>
                      </Tooltip>
                    </ClickNHold>
                  )}
                </React.Fragment>
              ))}
            </div>
          </Styles.MobileOptions>
        )}
      </div>
      <SlidingPanels size={match ? 93 : 78} isOpen={openConfigurationPanel} isClose={setOpenConfigurationPanel}>
        {openConfigurationPanel && (
          <SettingPanel
            data={data}
            menuOptions={menuOptions}
            openConfigurationPanel={openConfigurationPanel}
            setOpenConfigurationPanel={setOpenConfigurationPanel}
            setDeletedIds={() => setDeletedIds((prev) => [...prev, data.id])}
            toggle={toggle}
            managerWhRefetch={managerWhRefetch}
            managerVmIsFetching={managerVmIsFetching}
          />
        )}
      </SlidingPanels>
    </Styles.MobileRoot>
  );
};

export default React.memo(ManageWhMobile);
